// A few numbers about a sound, enough to tell a kick from a hat: where its
// energy sits (spectral centroid), how noisy it is (flatness), how much is
// low (sub share), how fast it dies (decay), how many hits it has (onsets).
// Pure over mono samples; the upload pipeline hands it the mono it already
// decoded for the tempo.

import { fftOf, hannWindow } from "@web-shared/lib/stretch/fft";
import { detectOnsets } from "@web-shared/lib/stretch/onsets";

export interface AudioFeatures {
  durationSec: number;
  /** Hz, energy-weighted mean frequency over the loud frames. */
  centroidHz: number;
  /** 0..1, geometric / arithmetic mean of the spectrum: 1 is white noise. */
  flatness: number;
  /** Share of energy below 150 Hz, 0..1. */
  lowShare: number;
  /** Seconds from the peak until the envelope is 20 dB down (Infinity when it never is). */
  decaySec: number;
  onsets: number;
  peak: number;
}

const FRAME = 2048;
const LOW_HZ = 150;

export function audioFeatures(mono: Float32Array, sampleRate: number): AudioFeatures | null {
  const n = mono.length;
  if (!(sampleRate > 0) || n < FRAME) return null;
  const durationSec = n / sampleRate;
  let peak = 0;
  for (let i = 0; i < n; i++) peak = Math.max(peak, Math.abs(mono[i]!));
  if (peak === 0) return null;

  const fft = fftOf(FRAME);
  const window = hannWindow(FRAME);
  const hop = FRAME >> 1;
  const bins = FRAME >> 1;
  const binHz = sampleRate / FRAME;
  const lowBin = Math.max(1, Math.round(LOW_HZ / binHz));
  const re = new Float64Array(FRAME);
  const im = new Float64Array(FRAME);
  let centroidSum = 0, flatSum = 0, lowSum = 0, totalSum = 0, frames = 0;
  // Frames quieter than 1/50 of the peak carry little: skip them so a
  // one-shot's tail does not read as silence-coloured noise.
  const floor = (peak * peak) / 2500;
  for (let start = 0; start + FRAME <= n; start += hop) {
    let energy = 0;
    for (let i = 0; i < FRAME; i++) {
      const v = mono[start + i]! * window[i]!;
      re[i] = v;
      im[i] = 0;
      energy += v * v;
    }
    if (energy / FRAME < floor) continue;
    fft.forward(re, im);
    let mag = 0, weighted = 0, logSum = 0, low = 0;
    for (let k = 1; k < bins; k++) {
      const p = re[k]! * re[k]! + im[k]! * im[k]!;
      mag += p;
      weighted += p * k * binHz;
      logSum += Math.log(p + 1e-12);
      if (k < lowBin) low += p;
    }
    if (mag <= 0) continue;
    centroidSum += weighted / mag;
    flatSum += Math.exp(logSum / (bins - 1)) / (mag / (bins - 1));
    lowSum += low;
    totalSum += mag;
    frames++;
  }
  if (frames === 0) return null;

  // Decay: RMS over 10 ms blocks, from the loudest block to −20 dB.
  const block = Math.max(1, Math.round(sampleRate / 100));
  const rms: number[] = [];
  for (let s = 0; s < n; s += block) {
    let e = 0;
    const end = Math.min(n, s + block);
    for (let i = s; i < end; i++) e += mono[i]! * mono[i]!;
    rms.push(Math.sqrt(e / (end - s)));
  }
  let peakAt = 0;
  for (let i = 1; i < rms.length; i++) if (rms[i]! > rms[peakAt]!) peakAt = i;
  const target = rms[peakAt]! / 10;
  let decayBlocks = Infinity;
  for (let i = peakAt; i < rms.length; i++) {
    if (rms[i]! <= target) {
      decayBlocks = i - peakAt;
      break;
    }
  }
  const onsets = detectOnsets({ sampleRate, numberOfChannels: 1, length: n, getChannelData: () => mono }).length;

  return {
    durationSec,
    centroidHz: centroidSum / frames,
    flatness: flatSum / frames,
    lowShare: totalSum > 0 ? lowSum / totalSum : 0,
    decaySec: decayBlocks === Infinity ? Infinity : (decayBlocks * block) / sampleRate,
    onsets,
    peak,
  };
}

/**
 * Which drum a one-shot is, by where it sits and how fast it goes — rough
 * cuts a producer would make by ear in a second. Null when nothing fits.
 */
export function drumGuess(f: AudioFeatures): string | null {
  const short = f.durationSec <= 1.5 && f.onsets <= 2;
  if (short) {
    if (f.centroidHz < 500 && f.lowShare > 0.4) return "drums:kick";
    if (f.centroidHz > 5000 && f.decaySec < 0.2) return "drums:hihat";
    if (f.centroidHz > 3500 && f.decaySec >= 0.5) return "drums:cymbal";
    if (f.centroidHz >= 800 && f.centroidHz <= 4500 && f.flatness > 0.15 && f.decaySec < 0.5) return "drums:snare";
    if (f.centroidHz >= 200 && f.centroidHz < 1500 && f.flatness < 0.15 && f.decaySec < 0.6) return "drums:tom";
    return null;
  }
  if (f.onsets >= 8 && f.flatness > 0.1 && f.durationSec >= 1.5) return "drums:drum-loop";
  return null;
}
